import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import {
    KeychainAccess,
    SecureStorage
} from "@aparajita/capacitor-secure-storage";
import { normalizeCustomerID } from "@/classes/customerID";

// import { createToaster } from "@meforma/vue-toaster";
// const toaster = createToaster({
//     /* options */
// });

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";
const CUSTOMER_ID_KEY = "customerID";
const SECURE_TOKEN_PREFIX = "forkto.auth";
let currentCustomerIDCache = null;

function isNativeStorageAvailable() {
    return Capacitor.isNativePlatform();
}

async function getCurrentCustomerID() {
    if (currentCustomerIDCache) return currentCustomerIDCache;
    try {
        const { value } = await Preferences.get({ key: CUSTOMER_ID_KEY });
        currentCustomerIDCache = normalizeCustomerID(value) || null;
        if (value && currentCustomerIDCache !== value) {
            await Preferences.set({ key: CUSTOMER_ID_KEY, value: currentCustomerIDCache });
        }
        return currentCustomerIDCache;
    } catch {
        return null;
    }
}

async function getSecureKey(key) {
    const customerID = await getCurrentCustomerID();
    if (!customerID) return null;
    return `${SECURE_TOKEN_PREFIX}.${customerID}.${key}`;
}

async function getSecureValue(key) {
    if (!isNativeStorageAvailable()) return null;

    const secureKey = await getSecureKey(key);
    if (!secureKey) return null;

    const value = await SecureStorage.get(secureKey, false);
    if (value === null || value === undefined) return null;
    return String(value);
}

async function setSecureValue(key, value) {
    if (!isNativeStorageAvailable()) return false;

    const secureKey = await getSecureKey(key);
    if (!secureKey) return false;

    await SecureStorage.set(
        secureKey,
        value,
        false,
        undefined,
        KeychainAccess.whenUnlockedThisDeviceOnly
    );
    return true;
}

async function removeSecureValue(key) {
    if (!isNativeStorageAvailable()) return;

    const secureKey = await getSecureKey(key);
    if (!secureKey) return;

    await SecureStorage.remove(secureKey);
}

async function setTokenValue(key, value) {
    if (isNativeStorageAvailable()) {
        try {
            const storedSecurely = await setSecureValue(key, value);
            if (!storedSecurely) {
                throw new Error("Secure token storage requires a tenant");
            }
            // Remove any value left by an older release that allowed native
            // credentials to fall back to unencrypted Preferences.
            await Preferences.remove({ key });
            return;
        } catch (error) {
            console.error("Secure token storage unavailable:", error);
            throw error;
        }
    }

    // Preferences remains the intended persistence layer for the browser.
    await Preferences.set({ key, value });
}

async function getTokenValue(key) {
    if (isNativeStorageAvailable()) {
        let secureValue = null;
        try {
            secureValue = await getSecureValue(key);
        } catch (error) {
            console.error("Secure token read failed:", error);
            throw error;
        }

        if (secureValue !== null) {
            // Clean up plaintext fallback data left by older releases, even
            // when a secure value already exists.
            await Preferences.remove({ key });
            return secureValue;
        }

        // One-time migration for credentials written by older app versions.
        // Never return the plaintext value unless secure migration succeeds.
        const { value } = await Preferences.get({ key });
        if (value !== null && value !== undefined) {
            const migrated = await setSecureValue(key, value);
            if (!migrated) {
                throw new Error("Secure token migration requires a tenant");
            }
            await Preferences.remove({ key });
        }

        return value;
    }

    const { value } = await Preferences.get({ key });
    return value;
}

async function removeTokenValue(key) {
    try {
        await removeSecureValue(key);
    } catch {
        // Continue removing legacy fallback.
    }
    await Preferences.remove({ key });
}

// Token functions
const setTokenInStorage = async (token) => {
    try {
        await setTokenValue(TOKEN_KEY, token);
    } catch (error) {
        console.error("Failed to set token in storage:", error);
        throw error;
    }
};

const getTokenInStorage = async () => {
    try {
        return await getTokenValue(TOKEN_KEY);
    } catch (error) {
        console.error("Failed to get token from storage:", error);
        return null;
    }
};

const removeTokenInStorage = async () => {
    try {
        await removeTokenValue(TOKEN_KEY);
    } catch (error) {
        console.error("Failed to remove token from storage:", error);
    }
};

// Refresh Token functions
const setRefreshTokenInStorage = async (token) => {
    try {
        await setTokenValue(REFRESH_TOKEN_KEY, token);
    } catch (error) {
        console.error("Failed to set refresh token in storage:", error);
        throw error;
    }
};

const getRefreshTokenInStorage = async () => {
    try {
        return await getTokenValue(REFRESH_TOKEN_KEY);
    } catch (error) {
        console.error("Failed to get refresh token from storage:", error);
        return null;
    }
};

const removeRefreshTokenInStorage = async () => {
    try {
        await removeTokenValue(REFRESH_TOKEN_KEY);
    } catch (error) {
        console.error("Failed to remove refresh token from storage:", error);
    }
};

// CustomerID functions
const setCustomerIDInStorage = async (customerID) => {
    try {
        const normalizedCustomerID = normalizeCustomerID(customerID);
        currentCustomerIDCache = normalizedCustomerID || null;
        await Preferences.set({
            key: CUSTOMER_ID_KEY,
            value: normalizedCustomerID
        });
    } catch (error) {
        console.error("Failed to set customer ID in storage:", error);
    }
};

const getCustomerIDInStorage = async () => {
    try {
        const { value } = await Preferences.get({ key: CUSTOMER_ID_KEY });
        const normalizedCustomerID = normalizeCustomerID(value);
        currentCustomerIDCache = normalizedCustomerID || null;
        if (value && normalizedCustomerID !== value) {
            await Preferences.set({ key: CUSTOMER_ID_KEY, value: normalizedCustomerID });
        }
        return normalizedCustomerID;
    } catch (error) {
        console.error("Failed to get customer ID from storage:", error);
        return null;
    }
};

const removeCustomerIDInStorage = async () => {
    try {
        currentCustomerIDCache = null;
        await Preferences.remove({ key: CUSTOMER_ID_KEY });
    } catch (error) {
        console.error("Failed to remove customer ID from storage:", error);
    }
};

export {
    setTokenInStorage,
    getTokenInStorage,
    removeTokenInStorage,
    setRefreshTokenInStorage,
    getRefreshTokenInStorage,
    removeRefreshTokenInStorage,
    setCustomerIDInStorage,
    getCustomerIDInStorage,
    removeCustomerIDInStorage
};
